import { userContext } from '../userManager';

export const USER_TYPES_ADMIN = 'PlatformAdmin';
export const USER_TYPES_FINANCIALADVISOR = 'FinancialAdvisor';
export const USER_TYPES_CLIENT = 'Client';

/* Users */

function getUserTypeUrl(type) {
  switch (type) {
    case USER_TYPES_CLIENT:
      return '/clients';
    case USER_TYPES_ADMIN:
      return '/admins';
    case USER_TYPES_FINANCIALADVISOR:
      return '/financialadvisors';
    default:
      return undefined;
  }
}

export const apiUsersGet = () => userContext.apiCall({ url: '/users' });

export const apiUserGet = (id) => userContext.apiCall({ url: `/users/${id}` });

export const apiUserPost = ({
  birthDate,
  crmOwnerId,
  email,
  gender,
  name,
  phoneNumber,
  phonePrefix,
  surname,
  title,
  type,
}) => {
  const body = {
    title,
    name,
    lastname: surname,
    sex: gender,
    email,
    phoneNumber,
    phonePrefix,
    dateOfBirth: birthDate,
    crmId: crmOwnerId,
    thumbUrl: '',
    profileUrl: '',
  };

  const url = getUserTypeUrl(type);
  if (!url) return undefined;

  return userContext.apiCall({ url, method: 'POST', body });
};

export const apiUserPatch = (
  id,
  {
    title,
    name,
    surname,
    phonePrefix,
    phoneNumber,
    gender,
    birthDate,
    placeOfBirth,
    citizenship,
    taxCode,
    countryOfBirthId,
    allowMarketingEmails,
    politicallyExposedPerson,
    politicalOccupation,
    politicallyExposedRelative,
    politicallyExposedRelativeRelationship,
    politicallyExposedRelativeOccupation,
    status,
    preferredLanguage,
  }
) => {
  const body = {
    title,
    name,
    lastname: surname,
    phonePrefix,
    phoneNumber,
    sex: gender,
    dateOfBirth: birthDate,
    placeOfBirth,
    citizenship,
    taxCode,
    countryOfBirthId,
    allowMarketingEmails,
    politicallyExposedPerson,
    politicalOccupation,
    politicallyExposedRelative,
    politicallyExposedRelativeRelationship,
    politicallyExposedRelativeOccupation,
    status,
    preferredLanguage,
  };

  return userContext.apiCall({ url: `/users/${id}`, method: 'PATCH', body });
};

export const apiUserMainContactsPatch = async ({ id, email, phonePrefix, phoneNumber, otp }) => {
  return userContext.apiCall({
    url: `/users/${id}/main-contacts`,
    method: 'PATCH',
    body: {
      email,
      phonePrefix,
      phoneNumber,
      otp: otp ?? null,
    },
  });
};

export const apiUserDetailsGet = async ({ id, userType }) => {
  // there a no admin details for now
  if (userType === USER_TYPES_ADMIN) return undefined;

  let url = getUserTypeUrl(userType);
  if (!url) return undefined;

  url = `${url}/${id}/details`;
  const res = await userContext.apiCall({ url });

  // TODO: remove after BE fix
  if (res.clientDetail) return { detail: res.clientDetail };
  return res;
};

export const apiUserDetailsPatch = async ({ id, userType, details }) => {
  // there a no admin details for now
  if (userType === USER_TYPES_ADMIN) return undefined;

  let url = getUserTypeUrl(userType);
  if (!url) return undefined;

  url = `${url}/${id}/details`;
  const res = await userContext.apiCall({ url, method: 'PATCH', body: details });

  // TODO: remove after BE fix
  if (res.clientDetail) return { detail: res.clientDetail };
  return res;
};

export const apiUserLanguagePatch = (id, { preferredLanguage }) =>
  userContext.apiCall({
    url: `/users/${id}`,
    method: 'PATCH',
    body: {
      preferredLanguage,
    },
  });

/* addresses */

export const apiUserAddressPost = (
  userId,
  { name, address, streetName, postCode, city, province, countryId, isResidential }
) => {
  const body = {
    addressType: name,
    streetName: address ?? streetName,
    region: province,
    postCode,
    city,
    province,
    countryId,
    isResidential,
  };
  return userContext.apiCall({ url: `/users/${userId}/addresses`, method: 'POST', body });
};

export const apiUserAddressesGet = (id) => userContext.apiCall({ url: `/users/${id}/addresses` });

/* documents */

export const apiUserDocumentsGet = (userId) =>
  userContext.apiCall({ url: `/users/${userId}/documents` });

export const apiUserDocumentPost = (userId, { expirationDate, ...params }) => {
  const body = { expiryDate: expirationDate, ...params };
  return userContext.apiCall({ url: `/users/${userId}/documents`, method: 'POST', body });
};

/* notifications */

export const apiUserNotifications = (id) =>
  userContext.apiCall({ url: `/users/${id}/notifications` });

// passing an empty body makes all notifications as read
export const apiUserNotificationsRead = (userId) =>
  userContext.apiCall({
    url: `/users/${userId}/notifications`,
    method: 'PUT',
    body: {},
    noResBody: true,
  });

export const apiUserAccountsGet = (userId) =>
  userContext.apiCall({
    url: `/users/${userId}/accounts`,
  });

/* pin */

export const apiUserPinPost = (userId, { pin }) =>
  userContext.apiCall({
    url: `/users/${userId}/pin`,
    method: 'POST',
    noResBody: true,
    body: { pin },
  });

export const apiUserPinPatch = (userId, { pin }) =>
  userContext.apiCall({
    url: `/users/${userId}/pin`,
    method: 'PATCH',
    noResBody: true,
    body: { pin },
  });

/* profile image */

export const apiUserProfileImagePost = (userId, { profileImage }) => {
  const formData = new FormData();
  formData.append(
    'file',
    new File([profileImage.file || profileImage], profileImage.name || profileImage)
  );

  return userContext.apiCall({
    url: `/users/${userId}/profile-image`,
    method: 'POST',
    file: formData,
  });
};

export const apiUserProfileImageGet = (userId) =>
  userContext.apiCall({ url: `/users/${userId}/profile-image`, method: 'GET' });

export const apiUserProfileImageDelete = (userId) =>
  userContext.apiCall({ url: `/users/${userId}/profile-image`, method: 'DELETE' });

/* watchlist */

export const apiUserWatchlistsGet = (userId) =>
  userContext.apiCall({ url: `/users/${userId}/watchlists`, method: 'GET' });

export const apiUserWatchlistsPost = (userId, { name, isDefault = false }) =>
  userContext.apiCall({
    url: `/users/${userId}/watchlists`,
    method: 'POST',
    body: { name, isDefault },
  });

export const apiUserWatchlistsInstrumentsGet = (userId) =>
  userContext.apiCall({ url: `/users/${userId}/watchlist-instruments`, method: 'GET' });

/* meetings */
export const apiUserMeetingsGet = (userId, start, end, email = '') => {
  return userContext.apiCall({
    url: `/users/${userId}/meetings?start=${start}&end=${end}&email=${email}`,
    method: 'GET',
  });
};

export const apiUserMeetingTypesGet = (userId) => {
  return userContext.apiCall({
    url: `/users/${userId}/meeting-types`,
    method: 'GET',
  });
};
